<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <oxd-table-filter :filter-title="$t('attendance.attendance_adjustment')">
      <oxd-form @submit-valid="filterItems" @reset="filterItems">
        <oxd-form-row>
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <date-input
                v-model="filters.fromDate"
                :label="$t('general.from_date')"
                :rules="rules.fromDate"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <date-input
                v-model="filters.toDate"
                :label="$t('general.to_date')"
                :rules="rules.toDate"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="filters.statuses"
                value="Select"
                type="multiselect"
                :label="$t('attendance.attendance_my_adjustment_status')"
                :options="adjustmentStatuses"
                :rules="rules.statuses"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-divider />

        <oxd-form-actions>
          <oxd-button
            display-type="ghost"
            :label="$t('general.reset')"
            type="reset"
          />
          <oxd-button
            class="orangehrm-left-space"
            display-type="secondary"
            :label="$t('general.search')"
            type="submit"
          />
        </oxd-form-actions>
      </oxd-form>
    </oxd-table-filter>
    <br />
    <div class="orangehrm-paper-container">
      <div class="orangehrm-header-container">
        <oxd-button
          :label="$t('general.add')"
          icon-name="plus"
          display-type="secondary"
          @click="onAddAdjustment"
        />
      </div>
      <table-header
        :selected="checkedItems.length"
        :total="total"
        :loading="isLoading"
      ></table-header>
      <div class="orangehrm-container">
        <oxd-card-table
          ref="cardTable"
          v-model:selected="checkedItems"
          v-model:order="sortDefinition"
          :headers="headers"
          :items="items?.data"
          :loading="isLoading"
          class="orangehrm-employee-list"
          row-decorator="oxd-table-decorator-card"
        />
      </div>
      <div class="orangehrm-bottom-container">
        <oxd-pagination
          v-if="showPaginator"
          v-model:current="currentPage"
          :length="pages"
        />
      </div>
    </div>
  </div>
  <attendance-adjustment-comments-modal
    v-if="showCommentModal"
    :id="commentModalState"
    @close="onCommentModalClose"
  />
</template>

<script>
// import usei18n from '@/core/util/composable/usei18n';
import usePaginate from '@ohrm/core/util/composable/usePaginate';
import {APIService} from '@/core/util/services/api.service';
import {computed, ref} from 'vue';
import moment from 'moment-timezone';
import useMyAdjustmentActions from '@/orangehrmAttendancePlugin/util/useMyAdjustmentActions';
import {
  endDateShouldBeAfterStartDate,
  required,
  validDateFormat,
} from '@/core/util/validation/rules';
import usei18n from '@/core/util/composable/usei18n';
import useDateFormat from '@/core/util/composable/useDateFormat';
import useLocale from '@/core/util/composable/useLocale';
import {formatEmployeePunchTime} from '@/orangehrmAttendancePlugin/util/formatEmployeePunchTime';
import {navigate} from '@/core/util/helper/navigation';
import AttendanceAdjustmentCommentsModal from '@/orangehrmAttendancePlugin/components/AttendanceAdjustmentCommentsModal';
import ShiftPermissionCommentCountCell from '@/orangehrmLeavePlugin/components/ShiftPermissionCommentCountCell';

const defaultFilters = {
  fromDate: null,
  toDate: null,
  statuses: [],
};

export default {
  components: {
    'attendance-adjustment-comments-modal': AttendanceAdjustmentCommentsModal,
  },
  props: {
    fromDate: {
      type: String,
      default: null,
    },
    toDate: {
      type: String,
      default: null,
    },
  },
  setup(props) {
    const {$t} = usei18n();
    const {userDateFormat, jsDateFormat, jsTimeFormat} = useDateFormat();
    const {locale} = useLocale();

    const filters = ref({
      ...defaultFilters,
      fromDate:
        props.fromDate || moment().startOf('month').format('YYYY-MM-DD'),
      toDate: props.toDate || moment().format('YYYY-MM-DD'),
    });
    const dataNormalizer = (data) => {
      return data.map((item) => {
        const punchInTime = formatEmployeePunchTime({
          utcTime: item.punchInUtcTime,
          timezoneName: item.punchInTimezoneName,
          userTime: item.punchInUserTime,
          jsDateFormat,
          jsTimeFormat,
          locale,
        });
        const punchOutTime = formatEmployeePunchTime({
          utcTime: item.punchOutUtcTime,
          timezoneName: item.punchOutTimezoneName,
          fallbackTimezoneName: item.punchInTimezoneName,
          userTime: item.punchOutUserTime,
          jsDateFormat,
          jsTimeFormat,
          locale,
        });

        return {
          id: item.id,
          employeeId: item.employeeId,
          employeeAttendanceId: item.employeeAttendanceId,
          punchInTimezoneName: item.punchInTimezoneName,
          punchInUserTime: item.punchInUserTime,
          punchOutTimezoneName: item.punchOutTimezoneName,
          punchOutUserTime: item.punchOutUserTime,
          punchInTime: punchInTime,
          punchOutTime: punchOutTime,
          note: item.note,
          status: item.status,
          statusName: item.statusName,
          commentCount: item.commentCount ?? 0,
          actions: [
            {
              action: 'CANCEL',
              name: 'Cancel',
            },
          ],
        };
      });
    };
    const adjustmentStatuses = [
      {
        id: 1,
        param: '1',
        label: 'Pending',
      },
      {
        id: 2,
        param: '2',
        label: 'Sent',
      },
      {
        id: 3,
        param: '3',
        label: 'Revision',
      },
      {
        id: 4,
        param: '4',
        label: 'Rejected',
      },
      {
        id: 5,
        param: '5',
        label: 'Approved',
      },
      {
        id: 6,
        param: '6',
        label: 'Canceled',
      },
    ];

    const statusMap = adjustmentStatuses.reduce((acc, cur) => {
      acc[cur.label] = Number(cur.param || 0);
      return acc;
    }, {});
    const serializedFilters = computed(() => {
      const statuses = Array.isArray(filters.value.statuses)
        ? filters.value.statuses
        : [];

      return {
        fromDate: filters.value.fromDate,
        toDate: filters.value.toDate,
        statuses: statuses.map((item) => item.id),
      };
    });
    const rules = {
      fromDate: [required, validDateFormat(userDateFormat)],
      toDate: [
        required,
        validDateFormat(userDateFormat),
        endDateShouldBeAfterStartDate(
          () => filters.value.fromDate,
          $t('general.to_date_should_be_after_from_date'),
          {allowSameDate: true},
        ),
      ],
    };
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/attendance/adjust',
    );

    const {myAdjustmentActions, processMyAdjustmentAction} =
      useMyAdjustmentActions(http);

    const {
      showPaginator,
      currentPage,
      total,
      pages,
      pageSize,
      response,
      isLoading,
      execQuery,
    } = usePaginate(http, {
      query: serializedFilters,
      normalizer: dataNormalizer,
    });

    return {
      http,
      showPaginator,
      currentPage,
      isLoading,
      total,
      pages,
      pageSize,
      execQuery,
      items: response,
      filters,
      rules,
      adjustmentStatuses,
      statusMap,
      myAdjustmentActions,
      processMyAdjustmentAction,
    };
  },
  data() {
    return {
      checkedItems: [],
      showCommentModal: false,
      commentModalState: null,
    };
  },
  computed: {
    headers() {
      return [
        {
          name: 'punchInTime',
          title: this.$t('attendance.punch_in'),
          sortField: 'punchInTime',
          style: {flex: 1},
        },
        {
          name: 'punchOutTime',
          title: this.$t('attendance.punch_out'),
          sortField: 'punchOutTime',
          style: {flex: 1},
        },
        {
          name: 'punchInTimezoneName',
          title: this.$t('attendance.timezone_in'),
          sortField: 'punchInTimezoneName',
          style: {flex: 1},
        },
        {
          name: 'punchOutTimezoneName',
          title: this.$t('attendance.timezone_out'),
          sortField: 'punchOutTimezoneName',
          style: {flex: 1},
        },
        {
          name: 'note',
          title: this.$t('general.note'),
          sortField: 'note',
          style: {flex: 1},
        },
        {
          name: 'statusName',
          title: this.$t('general.status'),
          sortField: 'statusName',
          style: {flex: 1},
        },
        {
          name: 'commentCount',
          title: this.$t('general.comments'),
          style: {flex: '0 0 9rem'},
          cellRenderer: this.commentCountCellRenderer,
        },
        {
          name: 'action',
          slot: 'footer',
          title: this.$t('general.actions'),
          cellType: 'oxd-table-cell-actions',
          cellRenderer: this.cellRenderer,
          style: {
            flex: '10%',
          },
        },
      ];
    },
  },
  beforeMount() {
    if (this.filters.statuses.length === 0) {
      this.filters.statuses = this.leaveStatuses;
    }
  },
  methods: {
    commentCountCellRenderer(index, count, header, row) {
      return {
        component: ShiftPermissionCommentCountCell,
        props: {
          count: count ?? 0,
          onClick: () => this.openCommentsModal(row),
        },
      };
    },
    openCommentsModal(row) {
      this.commentModalState = row.id;
      this.showCommentModal = true;
    },
    onCommentModalClose() {
      this.commentModalState = null;
      this.showCommentModal = false;
      this.execQuery();
    },
    cellRenderer(...[, , , row]) {
      const cellConfig = {};
      const {edit, cancel} = this.myAdjustmentActions;
      row.actions.map((item) => {
        if (
          item.action === 'EDIT' &&
          row.statusName.toLowerCase() === 'pending'
        ) {
          edit.props.label = this.$t('general.edit');
          edit.props.onClick = () => this.onMyAdjustmentAction(row, 'edit');
          cellConfig.edit = edit;
        }
        if (
          item.action === 'CANCEL' &&
          row.statusName.toLowerCase() === 'pending'
        ) {
          cancel.props.label = this.$t('general.cancel');
          cancel.props.onClick = () => this.onMyAdjustmentAction(row, 'cancel');
          cellConfig.cancel = cancel;
        }
      });
      return {
        props: {
          header: {
            cellConfig,
          },
        },
      };
    },
    toMyAdjustmentCanceled(row) {
      return {
        employeeAttendanceId: Number(row.employeeAttendanceId) || '',
        note: row.note || '',
        punchInTimezoneName: row.punchInTimezoneName || '',
        punchInUserTime: row.punchInUserTime || '',
        punchOutTimezoneName: row.punchOutTimezoneName || '',
        punchOutUserTime: row.punchOutUserTime || '',
        submissionStatus: Number(row.submissionStatus),
      };
    },

    onMyAdjustmentAction(row, type) {
      if (type === 'cancel') {
        this.isLoading = true;
        const payload = this.toMyAdjustmentCanceled(row);
        this.processMyAdjustmentAction(
          row.id,
          payload,
          this.statusMap['Canceled'],
        )
          .then(() => {
            return this.$toast.updateSuccess();
          })
          .finally(this.resetDataTable);
      } else {
        const id = row.id;
        navigate(`/attendance/editAttendanceAdjustment/${id}`);
      }
    },
    onAddAdjustment() {
      navigate(`/attendance/addAttendanceAdjustment`);
    },
    async resetDataTable() {
      this.checkedItems = [];
      await this.execQuery();
    },
    async filterItems() {
      await this.execQuery();
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep(.card-footer-slot) {
  .oxd-table-cell-actions {
    justify-content: flex-end;
  }

  .oxd-table-cell-actions > * {
    margin: 0 !important;
  }
}

.oxd-export-btn {
  margin-right: 4px;
}
</style>
